import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE } from "@/components/aklive/motion";
import { PlaceholderBlock } from "@/components/aklive/PlaceholderBlock";

export const Lightbox = ({ items, index, onNavigate, onClose }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const touchX = useRef(null);
  const item = items[index];

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector("[data-lightbox-close]")?.focus();
    return () => {
      document.body.style.overflow = "";
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowRight") {
        onNavigate(1);
      } else if (e.key === "ArrowLeft") {
        onNavigate(-1);
      } else if (e.key === "Tab") {
        const els = ref.current?.querySelectorAll("button");
        if (!els?.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNavigate, onClose]);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(delta) > 50) onNavigate(delta < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <motion.div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={`${item.name} — ${item.category}`}
      data-testid="work-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduce ? 0 : 0.3, ease: EASE }}
      className="fixed inset-0 z-[100] flex flex-col bg-coal"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between px-5 py-4 sm:px-8" onClick={(e) => e.stopPropagation()}>
        <span className="font-mono text-[10px] tracking-[0.2em] text-paper/50 md:text-xs" data-testid="lightbox-counter">
          {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
        </span>
        <button
          type="button"
          data-lightbox-close
          data-testid="lightbox-close-btn"
          aria-label="Close viewer"
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center border border-paper/20 text-xl text-paper transition-colors duration-300 hover:border-paper"
        >
          ×
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center px-5 sm:px-16" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.figure
            key={item.slug}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.25, ease: EASE }}
            className="m-0 w-full max-w-4xl"
          >
            <div className="max-h-[62vh]" style={{ aspectRatio: "3 / 2" }}>
              <PlaceholderBlock label={item.lightboxLabel} fill dark />
            </div>
            <figcaption className="mt-5 flex flex-wrap items-baseline gap-x-5 gap-y-1">
              <span className="font-display text-xl font-bold tracking-tight text-paper md:text-2xl" data-testid="lightbox-title">
                {item.name}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 md:text-xs" data-testid="lightbox-category">
                {item.category}
              </span>
            </figcaption>
          </motion.figure>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between px-5 py-5 sm:px-8" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          data-testid="lightbox-prev-btn"
          aria-label="Previous project"
          onClick={() => onNavigate(-1)}
          className="flex h-11 w-11 items-center justify-center border border-paper/20 text-paper transition-colors duration-300 hover:border-paper"
        >
          ←
        </button>
        <button
          type="button"
          data-testid="lightbox-next-btn"
          aria-label="Next project"
          onClick={() => onNavigate(1)}
          className="flex h-11 w-11 items-center justify-center border border-paper/20 text-paper transition-colors duration-300 hover:border-paper"
        >
          →
        </button>
      </div>
    </motion.div>
  );
};
